import { createContext, useContext } from 'react'

import { ADS_FORBIDDEN_TERMS } from '../../omensSeoTechnical/pages'

/**
 * Google Ads vocabulary (PRD section 4) on the GEO pages that receive ads: no "receita/prescrição", no medication
 * name. GeoPage sets it (off on the medication pages, and on the home and /ejaculacao-precoce, validated without
 * it); the shared sections (product cards, testimonials, the "how it works" text) read it and show the class of the
 * treatment instead. Off everywhere else (the other brands' pages, the campaign landings).
 */
export const AdsSafeContext = createContext(false)

export const useAdsSafe = () => useContext(AdsSafeContext)

/**
 * A GEO page (GeoPage, every page promoted from its validation copy; the copies themselves are gone since
 * 28/10/2026). The shared sections switch to the version Morgan validated (linked press logos, Reclame Aqui strip,
 * "Mensagem" subtitle…). Off on the other brands' pages and the campaign landings.
 */
export const GeoPreviewContext = createContext(false)

export const useGeoPreview = () => useContext(GeoPreviewContext)

export const hasForbiddenTerm = (text: string) => new RegExp(ADS_FORBIDDEN_TERMS.source, 'i').test(text)

/** Product names → the class of the treatment (PRD 4: "Spray de ação rápida", "Cápsulas de uso diário"). */
const PRODUCT_CLASS: Record<string, string> = {
  'Clomipramina spray': 'Spray de ação local',
  'Paroxetina spray': 'Spray de ação rápida',
  'Dapoxetina cápsulas': 'Cápsulas para usar antes da relação',
  'Paroxetina cápsulas': 'Cápsulas de uso diário',
  'Tadalafila + L-Arginina + Tribulus + Fentolamina spray': 'Spray para a ereção com fitoterápicos',
  'Tadalafila + Protodioscina spray': 'Spray para a ereção',
  'Sildenafila spray': 'Spray para a ereção de ação rápida',
  'Alprostradil gel': 'Gel tópico para a ereção',
  'Paroxetina + Tadalafila spray': 'Spray para ereção e controle da ejaculação',
  'Dapoxetina + Tadalafila cápsulas': 'Cápsulas para ereção e controle da ejaculação, antes da relação',
  'Clomipramina + Tadalafila cápsulas': 'Cápsulas para ereção e controle da ejaculação, de uso diário',
  'Finasterida + Saw Palmeto + Biotina cápsulas': 'Cápsulas para a queda de cabelo',
  'Minoxidil + Trichoxidil + TrichoSol spray': 'Spray para a queda de cabelo',
  'Minoxidil oral': 'Tratamento oral para a queda de cabelo',
  'Minoxidil,+ Trichoxidil,+ TrichoSol,em spray': 'Spray para a queda de cabelo',
  'Finasterida,+ Saw Palmeto,+ Biotina': 'Cápsulas para a queda de cabelo'
}

/** The label to show: the class when the name is a medication, unchanged otherwise. */
export const adsSafeLabel = (label: string | string[]): string | string[] => {
  const key = Array.isArray(label) ? label.join(',') : label
  if (PRODUCT_CLASS[key]) return PRODUCT_CLASS[key]
  return hasForbiddenTerm(key) ? 'Fórmula manipulada' : label
}

/** Bullet lines without the ones that name a medication or a prescription. */
export const adsSafeLines = (lines: string[]) => lines.filter((line) => !hasForbiddenTerm(line))
